import { useUserInfoStore } from '@/modules/login/store/userInfo';
import { useEffect, useState } from 'react';

/**
 * «جاباز» is rolled out to ONE doctor profile and only to its owner. Nobody else sees any of it, and no
 * other profile changes. Everything is decided on the client (after mount), so server rendering and the
 * shared caches of the profile stay exactly as they are (docs/performance.md: withCSR stays
 * user-independent).
 */
export const JABAZ_ROLLOUT_SLUGS: readonly string[] = ['دکتر-محمد-محمدی-125'];

/** Opening the profile once with `?jabaz=1` (or `?jabaz=0` to undo) opts this device in. */
const OPT_IN_KEY = 'jabaz-opt-in';

const decode = (slug: string) => {
  try {
    return decodeURIComponent(slug);
  } catch {
    return slug;
  }
};

export const isJabazRolloutSlug = (slug?: string | null) => !!slug && JABAZ_ROLLOUT_SLUGS.includes(decode(slug));

const readOptIn = () => {
  try {
    return window.localStorage.getItem(OPT_IN_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * Device opted in (see OPT_IN_KEY). With `onSlug`, the `?jabaz=1|0` of the address is applied first —
 * and only on the rollout profile; on any other page the address is never read and nothing is written.
 */
export const useJabazOptIn = (onSlug?: string | null) => {
  const [optedIn, setOptedIn] = useState(false);
  useEffect(() => {
    if (onSlug !== undefined && !isJabazRolloutSlug(onSlug)) return;
    const param = new URLSearchParams(window.location.search).get('jabaz');
    try {
      if (onSlug !== undefined && param === '1') window.localStorage.setItem(OPT_IN_KEY, '1');
      if (onSlug !== undefined && param === '0') window.localStorage.removeItem(OPT_IN_KEY);
    } catch {
      // storage blocked: fall back to the account check only
    }
    setOptedIn(readOptIn());
  }, [onSlug]);
  return optedIn;
};

/**
 * Outside the profile («نوبت‌های من»): true only on a device that opted in or already holds a seat in a
 * rollout doctor's queue. Read straight from localStorage so nobody else even downloads the Jabaz code.
 */
export const useJabazOnDevice = () => {
  const [on, setOn] = useState(false);
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem('jabaz-state');
      const slugs = Object.keys(JSON.parse(raw ?? '{}')?.state?.memberships ?? {});
      setOn(readOptIn() || slugs.some(isJabazRolloutSlug));
    } catch {
      setOn(readOptIn());
    }
  }, []);
  return on;
};

/**
 * On a doctor profile: Jabaz is on only for the rollout slug, and only for its owner — logged in with
 * the doctor's own account, or a device opted in with `?jabaz=1`.
 */
export const useJabazOnProfile = (slug?: string | null, doctorUserId?: string | number | null) => {
  const optedIn = useJabazOptIn(slug ?? null);
  const userId = useUserInfoStore(state => state.info?.id);
  const isLogin = useUserInfoStore(state => state.isLogin);
  if (!isJabazRolloutSlug(slug)) return false;
  const isOwner = isLogin && !!userId && doctorUserId != null && String(doctorUserId) === String(userId);
  return isOwner || optedIn;
};
